"use client";

import React from "react";
import {
  Badge,
  Box,
  Button,
  Combobox,
  createListCollection,
  Dialog,
  Field,
  Fieldset,
  HStack,
  IconButton,
  Input,
  NativeSelect,
  Portal,
  SimpleGrid,
  Spinner,
  Text,
  Card,
} from "@chakra-ui/react";
import { MdDeleteOutline, MdOutlineBadge, MdOutlineSearch, MdOutlineSupervisorAccount } from "react-icons/md";
import AppAvatar from "components/utils/AppAvatar";
import {
  useClearDashboardCampAccesoForUsuario,
  useDashboardCampAccesos,
  useSearchDashboardCampInstructores,
  useSetDashboardCampAccesoForUsuario,
} from "features/dashboard/camp/accesos/hooks";
import { resolveAvatarUrl } from "utils/avatar";

const toStr = (v) => (v == null ? "" : String(v));
const toNull = (v) => {
  const s = String(v ?? "").trim();
  return s.length ? s : null;
};

const AccesoCard = ({ correo, rol, instructorNombre, hasAcceso, avatarSeed, avatarSrc, onClick }) => {
  return (
    <Card.Root
      flexDirection="row"
      overflow="hidden"
      cursor="pointer"
      bg="dashboard.bg.surface"
      _hover={{ bg: "dashboard.bg.surfaceSubtle" }}
      onClick={onClick}
    >
      <Box p="4" display="flex" alignItems="center" justifyContent="center">
        <AppAvatar size="2xl" name={instructorNombre || correo} src={avatarSrc || undefined} seed={avatarSeed || correo} />
      </Box>
      <Card.Body>
        <Card.Title mb="1">{correo}</Card.Title>
        <Card.Description>
          <HStack gap="2" wrap="wrap">
            <Badge colorPalette={hasAcceso ? "green" : "red"}>{hasAcceso ? "Con acceso" : "Sin acceso"}</Badge>
            <Badge>{rol || "—"}</Badge>
            <Badge>{instructorNombre ? `Instructor: ${instructorNombre}` : "Sin instructor"}</Badge>
          </HStack>
        </Card.Description>
      </Card.Body>
    </Card.Root>
  );
};

const InstructorCombobox = ({ value, label, onChange, disabled }) => {
  const [query, setQuery] = React.useState(label || "");
  const q = useSearchDashboardCampInstructores(query, !disabled);

  const items = React.useMemo(() => {
    const rows = Array.isArray(q.data) ? q.data : [];
    return rows.map((r) => ({
      label: r.nombre,
      value: r.id,
      avatar: resolveAvatarUrl(r),
    }));
  }, [q.data]);

  const collection = React.useMemo(() => createListCollection({ items }), [items]);

  React.useEffect(() => {
    setQuery(label || "");
  }, [label]);

  return (
    <Combobox.Root
      collection={collection}
      value={value ? [value] : []}
      onValueChange={(e) => {
        const id = e.value?.[0] || "";
        const picked = items.find((x) => x.value === id);
        onChange(
          picked
            ? { id: picked.value, nombre: picked.label, fotoUrl: picked.avatar || null }
            : { id: null, nombre: "", fotoUrl: null }
        );
      }}
      onInputValueChange={(e) => setQuery(e.inputValue || "")}
      disabled={disabled}
      openOnClick
    >
      <Combobox.Label>Instructor (opcional)</Combobox.Label>

      <Combobox.Control bg="dashboard.bg.input" color="dashboard.fg.default" borderColor="dashboard.border.default">
        <Combobox.Input placeholder="Busca por nombre (mín 2 letras)" value={query} onChange={(e) => setQuery(e.target.value)} />
        <Combobox.Trigger />
      </Combobox.Control>

      <Portal>
        <Combobox.Positioner style={{ zIndex: 2000 }}>
          <Combobox.Content bg="dashboard.bg.popover" color="dashboard.fg.default" borderColor="dashboard.border.default">
            {items.length === 0 ? (
              <Box p="3" opacity="0.7">
                <Text>Sin resultados</Text>
              </Box>
            ) : (
              items.map((item) => (
                <Combobox.Item key={item.value} item={item} _highlighted={{ bg: "dashboard.bg.surfaceMuted" }}>
                  <HStack gap="2">
                    <AppAvatar size="2xs" name={item.label} src={item.avatar} seed={item.value || item.label} />
                    <Combobox.ItemText>{item.label}</Combobox.ItemText>
                  </HStack>
                  <Combobox.ItemIndicator />
                </Combobox.Item>
              ))
            )}
          </Combobox.Content>
        </Combobox.Positioner>
      </Portal>
    </Combobox.Root>
  );
};

const CampAccesos = () => {
  const q = useDashboardCampAccesos(true);
  const setM = useSetDashboardCampAccesoForUsuario();
  const clearM = useClearDashboardCampAccesoForUsuario();

  const [search, setSearch] = React.useState("");

  const rows = React.useMemo(() => {
    const raw = Array.isArray(q.data?.rows) ? q.data.rows : Array.isArray(q.data) ? q.data : [];
    const needle = String(search || "").trim().toLowerCase();
    const filtered = needle.length ? raw.filter((r) => String(r.correo || "").toLowerCase().includes(needle)) : raw;

    return filtered.map((r, i) => {
      const accesos = Array.isArray(r.accesos) ? r.accesos : [];
      const instructorFotoUrl = r.instructorFotoUrl ?? r.fotoUrl ?? null;

      return {
        usuarioId: r.usuarioId,
        correo: r.correo || "—",
        instructorId: r.instructorId ?? null,
        instructorNombre: r.instructorNombre ?? "",
        instructorFotoUrl,
        rol: accesos[0]?.rol ?? "",
        hasAcceso: accesos.length > 0,
        avatarSeed: r.instructorId || r.correo || i,
      };
    });
  }, [q.data, search]);

  const totalUsuarios = rows.length;
  const totalConAcceso = rows.filter((x) => x.hasAcceso).length;

  const [open, setOpen] = React.useState(false);
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  const [editingUsuarioId, setEditingUsuarioId] = React.useState(null);
  const [fCorreo, setFCorreo] = React.useState("");
  const [fRol, setFRol] = React.useState("jefe");
  const [fInstructorId, setFInstructorId] = React.useState(null);
  const [fInstructorNombre, setFInstructorNombre] = React.useState("");
  const [fInstructorFoto, setFInstructorFoto] = React.useState(null);

  const saving = setM.isPending;
  const deleting = clearM.isPending;
  const error = setM.error || clearM.error;

  const openForUser = (item) => {
    setConfirmDelete(false);
    setEditingUsuarioId(item.usuarioId);
    setFCorreo(item.correo);
    setFRol(toStr(item.rol) || "jefe");
    setFInstructorId(item.instructorId ?? null);
    setFInstructorNombre(item.instructorNombre ?? "");
    setFInstructorFoto(item.instructorFotoUrl ?? null);
    setOpen(true);
  };

  const onSave = async () => {
    if (!editingUsuarioId) return;

    await setM.mutateAsync({
      usuarioId: editingUsuarioId,
      payload: { rol: toNull(fRol) || "jefe", instructorId: fInstructorId ?? null },
    });

    setOpen(false);
  };

  const onDeleteAll = async () => {
    if (!editingUsuarioId) return;
    await clearM.mutateAsync(editingUsuarioId);
    setOpen(false);
  };

  return (
    <Box>
      <HStack justifyContent="space-between" mb="4">
        <Box>
          <Text fontWeight="semibold" textStyle="4xl">Accesos</Text>
          <Text opacity="0.8">Asigna acceso al portal Camp por usuario.</Text>
        </Box>
        <IconButton aria-label="noop" variant="ghost" rounded="full" />
      </HStack>

      {q.isError ? (
        <pre style={{ whiteSpace: "pre-wrap", color: "crimson", marginBottom: 12 }}>
          {String(q.error?.message || q.error)}
        </pre>
      ) : null}

      <HStack mb="4" gap="3" alignItems="center">
        <HStack
          flex="1"
          bg="dashboard.bg.surfaceMuted"
          borderWidth="1px"
          borderColor="dashboard.border.muted"
          rounded="lg"
          px="3"
          py="2"
        >
          <MdOutlineSearch />
          <Input border="0" outline="0" bg="transparent" placeholder="Buscar por correo..." value={search} onChange={(e) => setSearch(e.target.value)} />
        </HStack>

        <HStack>
          <Badge colorPalette="gray">Usuarios: {totalUsuarios}</Badge>
          <Badge colorPalette="green">Con acceso: {totalConAcceso}</Badge>
        </HStack>
      </HStack>

      {q.isLoading ? (
        <Box py="10" display="flex" alignItems="center" justifyContent="center">
          <Spinner />
        </Box>
      ) : (
        <SimpleGrid columns={{ base: 1, md: 3 }} gap="2">
          {rows.map((item) => (
            <AccesoCard
              key={item.usuarioId}
              correo={item.correo}
              rol={item.rol}
              instructorNombre={item.instructorNombre}
              avatarSrc={item.instructorFotoUrl}
              hasAcceso={item.hasAcceso}
              avatarSeed={item.avatarSeed}
              onClick={() => openForUser(item)}
            />
          ))}
        </SimpleGrid>
      )}

      <Dialog.Root
        open={open}
        onOpenChange={(e) => {
          setOpen(e.open);
          if (!e.open) setConfirmDelete(false);
        }}
      >
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content maxW="lg" bg="dashboard.bg.dialog" color="dashboard.fg.default" borderWidth="1px" borderColor="dashboard.border.default">
            <Dialog.Header>
              <Dialog.Title>Editar accesos</Dialog.Title>
            </Dialog.Header>

            <Dialog.Body>
              <Fieldset.Root size="lg">
                <Fieldset.Content>
                  <Field.Root>
                    <Field.Label>Correo</Field.Label>
                    <Input value={fCorreo} isDisabled />
                  </Field.Root>

                  <Field.Root>
                    <Field.Label>Rol</Field.Label>
                    <NativeSelect.Root>
                      <NativeSelect.Field value={fRol} onChange={(e) => setFRol(e.target.value)}>
                        <option value="jefe">jefe</option>
                      </NativeSelect.Field>
                    </NativeSelect.Root>
                  </Field.Root>

                  <Box>
                    <InstructorCombobox
                      value={fInstructorId}
                      label={fInstructorNombre}
                      onChange={(x) => {
                        setFInstructorId(x.id);
                        setFInstructorNombre(x.nombre || "");
                        setFInstructorFoto(x.fotoUrl ?? null);
                      }}
                    />

                    <HStack mt="2" justifyContent="space-between">
                      <HStack opacity="0.8">
                        <AppAvatar size="xs" name={fInstructorNombre || fCorreo} src={fInstructorFoto || undefined} seed={fInstructorId || fInstructorNombre || fCorreo} />
                        <MdOutlineSupervisorAccount />
                        <Text fontSize="sm">{fInstructorId ? `Vinculado: ${fInstructorNombre}` : "Sin instructor vinculado"}</Text>
                      </HStack>

                      <Button variant="outline" onClick={() => { setFInstructorId(null); setFInstructorNombre(""); setFInstructorFoto(null); }} isDisabled={saving || deleting}>
                        <MdOutlineBadge />
                        <Text>Quitar instructor</Text>
                      </Button>
                    </HStack>
                  </Box>

                  {error ? (
                    <Text color="dashboard.feedback.error" whiteSpace="pre-wrap">
                      {String(error?.message || error)}
                    </Text>
                  ) : null}
                </Fieldset.Content>
              </Fieldset.Root>
            </Dialog.Body>

            <Dialog.Footer>
              <HStack justifyContent="space-between" w="full">
                <Box>
                  {confirmDelete ? (
                    <HStack>
                      <Button variant="outline" onClick={() => setConfirmDelete(false)} isDisabled={saving || deleting}>
                        Cancelar
                      </Button>
                      <Button colorPalette="red" variant="solid" onClick={onDeleteAll} isDisabled={saving || deleting}>
                        {deleting ? <Spinner size="sm" /> : null}
                        <Text>Quitar acceso</Text>
                      </Button>
                    </HStack>
                  ) : (
                    <Button variant="outline" onClick={() => setConfirmDelete(true)} isDisabled={saving || deleting}>
                      <MdDeleteOutline />
                      <Text>Eliminar</Text>
                    </Button>
                  )}
                </Box>

                <HStack>
                  <Button variant="outline" onClick={() => setOpen(false)} isDisabled={saving || deleting}>
                    Cancelar
                  </Button>
                  <Button colorPalette="red" onClick={onSave} isDisabled={saving || deleting}>
                    {saving ? <Spinner size="sm" /> : null}
                    <Text>Guardar</Text>
                  </Button>
                </HStack>
              </HStack>
            </Dialog.Footer>

            <Dialog.CloseTrigger />
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>
    </Box>
  );
};

export default CampAccesos;
